/**
 * Zugriff auf die API des Servers.
 *
 * Tritt an die Stelle der `window.crm`-Bridge, die es unter Electron gab. Der
 * Vertrag ist derselbe (`CrmApi` aus @epasu/core), deshalb funktionieren die
 * Seiten unverändert — sie rufen weiterhin `crm.contacts.list(...)`.
 *
 * Umgesetzt als Proxy statt als 52 handgeschriebene Methoden: jeder Aufruf
 * `crm.<namespace>.<method>(...args)` wird zu
 * `POST /api/<namespace>/<method>` mit `{ args }`. Neue Vertragsmethoden
 * funktionieren dadurch ohne Änderung an dieser Datei.
 */
import type { CrmApi } from '@epasu/core';

/** Wird geworfen, wenn die Sitzung abgelaufen ist. */
export class NotAuthenticatedError extends Error {
  constructor() {
    super('Nicht angemeldet.');
    this.name = 'NotAuthenticatedError';
  }
}

/** Wird gerufen, sobald der Server 401 meldet — meldet die Oberfläche ab. */
let onUnauthenticated: (() => void) | null = null;

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

interface ApiEnvelope<T> {
  ok: boolean;
  data?: T;
  error?: string;
}

async function call(namespace: string, method: string, args: unknown[]): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(`/api/${namespace}/${method}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      // Der Session-Cookie ist httpOnly; ohne credentials ginge er nicht mit.
      credentials: 'same-origin',
      body: JSON.stringify({ args }),
    });
  } catch {
    throw new Error('Keine Verbindung zum Server.');
  }

  if (response.status === 401) {
    onUnauthenticated?.();
    throw new NotAuthenticatedError();
  }

  let payload: ApiEnvelope<unknown>;
  try {
    payload = (await response.json()) as ApiEnvelope<unknown>;
  } catch {
    throw new Error(`Unerwartete Antwort vom Server (${response.status}).`);
  }

  if (!payload.ok) {
    throw new Error(payload.error ?? `Aufruf fehlgeschlagen (${response.status}).`);
  }
  return payload.data;
}

/**
 * Zwei Ebenen Proxy: die erste liefert den Namensraum, die zweite die Methode.
 *
 * `then` wird ausdrücklich ausgenommen. Sonst hielte `await crm.contacts` das
 * Objekt für ein Promise und riefe eine Methode namens `then` auf.
 */
function createClient(): CrmApi {
  const namespaces = new Map<string, Record<string, unknown>>();

  return new Proxy({} as CrmApi, {
    get(_target, namespace) {
      if (typeof namespace !== 'string' || namespace === 'then') return undefined;

      const zwischengespeichert = namespaces.get(namespace);
      if (zwischengespeichert) return zwischengespeichert;

      const gruppe = new Proxy({} as Record<string, unknown>, {
        get(_t, method) {
          if (typeof method !== 'string' || method === 'then') return undefined;
          return (...args: unknown[]) => call(namespace, method, args);
        },
      });

      namespaces.set(namespace, gruppe);
      return gruppe;
    },
  });
}

export const crm: CrmApi = createClient();

/**
 * Die Zeilentypen, wie sie tatsächlich über die API kommen — direkt aus dem
 * Vertrag abgeleitet, damit sie nicht auseinanderlaufen können.
 */
export type ContactRow = Awaited<ReturnType<CrmApi['contacts']['list']>>[number];
export type TaskRow = Awaited<ReturnType<CrmApi['tasks']['list']>>[number];
export type MailRow = Awaited<ReturnType<CrmApi['mailbox']['list']>>[number];
export type OrderRow = Awaited<ReturnType<CrmApi['orders']['list']>>[number];
export type OrganizationRow = Awaited<
  ReturnType<CrmApi['organizations']['list']>
>[number];
export type FileRow = Awaited<ReturnType<CrmApi['files']['list']>>[number];
export type CustomerRow = Awaited<ReturnType<CrmApi['customers']['list']>>[number];
export type ProjectRow = Awaited<ReturnType<CrmApi['projects']['list']>>[number];
export type ProjectNoteRow = Awaited<ReturnType<CrmApi['projects']['notes']>>[number];
export type TimelineRow = Awaited<ReturnType<CrmApi['projects']['timeline']>>[number];
export type FollowupRow = Awaited<ReturnType<CrmApi['followups']['list']>>[number];
export type LeadListRow = Awaited<ReturnType<CrmApi['leadLists']['list']>>[number];
export type CampaignRow = Awaited<ReturnType<CrmApi['campaigns']['list']>>[number];
export type CampaignStepRow = Awaited<ReturnType<CrmApi['campaigns']['steps']>>[number];
export type CampaignMemberRow = Awaited<ReturnType<CrmApi['campaigns']['members']>>[number];
export type WavePreviewDto = Awaited<ReturnType<CrmApi['campaigns']['wavePreview']>>;
export type WaveStartDto = Awaited<ReturnType<CrmApi['campaigns']['sendWave']>>;
export type PipelineMetaRow = Awaited<
  ReturnType<CrmApi['pipelines']['listWithMeta']>
>[number];
/**
 * Der Entwurf ohne das `null`, das `followups.draft` fuer eine unbekannte
 * Nachricht liefert — der Dialog bekommt ihn erst zu sehen, wenn er da ist.
 */
export type FollowupDraftDto = NonNullable<
  Awaited<ReturnType<CrmApi['followups']['draft']>>
>;
/** Das Ergebnis der Erstkontakt-Pruefung fuer einen einzelnen Empfaenger. */
export type OutreachCheckRow = Awaited<
  ReturnType<CrmApi['mail']['outreachCheck']>
>[number];

/**
 * Dateien hochladen.
 *
 * Läuft nicht über den JSON-Dispatcher: Eine PDF durch Base64 zu schicken
 * bläht sie um ein Drittel auf und zwingt beide Seiten, sie komplett im
 * Speicher zu halten.
 */
export async function uploadFiles(dateien: File[]): Promise<FileRow[]> {
  const form = new FormData();
  for (const d of dateien) form.append('file', d, d.name);

  const res = await fetch('/api/files/upload', { method: 'POST', body: form });
  const body = (await res.json()) as
    | { ok: true; data: FileRow[] }
    | { ok: false; error: string };

  if (!body.ok) throw new Error(body.error);
  return body.data;
}

/* -------------------------------------------------------------------------- */
/*  Anmeldung                                                                 */
/* -------------------------------------------------------------------------- */

export interface CurrentUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
}

async function authRequest<T>(
  path: string,
  init: RequestInit,
): Promise<ApiEnvelope<T>> {
  const response = await fetch(path, { credentials: 'same-origin', ...init });
  try {
    return (await response.json()) as ApiEnvelope<T>;
  } catch {
    return { ok: false, error: `Unerwartete Antwort vom Server (${response.status}).` };
  }
}

export async function login(
  email: string,
  password: string,
): Promise<{ ok: true; user: CurrentUser } | { ok: false; error: string }> {
  const result = await authRequest<CurrentUser>('/api/auth/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (result.ok && result.data) return { ok: true, user: result.data };
  return { ok: false, error: result.error ?? 'Anmeldung fehlgeschlagen.' };
}

export async function logout(): Promise<void> {
  await authRequest('/api/auth/logout', { method: 'POST' });
}

/** Liefert das angemeldete Konto, oder null. */
export async function fetchCurrentUser(): Promise<CurrentUser | null> {
  const result = await authRequest<CurrentUser>('/api/auth/me', { method: 'GET' });
  return result.ok && result.data ? result.data : null;
}
